import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/system';

import mixinSx from './mixinSx';
import { sharedShadow } from './styleUtils';

const baseTooltipStyles = {
  borderRadius: '10px',
  boxShadow: sharedShadow,
  zIndex: 2000,
  padding: '6px 8px',
  maxWidth: '320px',
  fontSize: '13px',
  fontWeight: 600,
};

// TODO: Delete this once ui-core/Tooltip is fully adopted
const baseArrowStyles = {
  '& .MuiTooltip-arrow': {
    display: 'none',
  },
};

const darkVariantStyles = {
  backgroundColor: (theme: Theme) => theme.palette.grey100,
  color: (theme: Theme) => theme.palette.common.white,
};

const lightVariantStyles = {
  backgroundColor: (theme: Theme) => theme.palette.white,
  color: (theme: Theme) => theme.palette.grey70,
};

const getDarkOutline = () => (theme: Theme) =>
  `1px solid ${theme.palette.grey10}16`;

const getLightOutline = () => (theme: Theme) =>
  `1px solid ${theme.palette.grey100}16`;

/**
 * Tooltip Utilities Overview:
 * - `tooltipDarkSlotProps()` and `tooltipLightSlotProps()`
 * - Uses `slotProps.popper` structure
 * - Uses explicit CSS properties like `fontSize`, `fontWeight`
 */

// ========================================
// SlotProps API Functions (for MUI v5.14+)
// ========================================

/**
 * Dark tooltip styling for MUI v5.14+ using slotProps API
 * Returns props compatible with tooltipProps on ui-core components
 */
export function tooltipDarkSlotProps(additionalSx?: SxProps<Theme>) {
  const baseTooltipSx = {
    ...baseTooltipStyles,
    ...darkVariantStyles,
    outline: getDarkOutline(),
    ...baseArrowStyles,
  };

  return {
    slotProps: {
      popper: {
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -8],
            },
          },
        ],
      },
      tooltip: {
        sx: mixinSx(baseTooltipSx, additionalSx),
      },
    },
  };
}

/**
 * Light tooltip styling for MUI v5.14+ using slotProps API
 * Returns props compatible with tooltipProps on ui-core components
 */
export function tooltipLightSlotProps(additionalSx?: SxProps<Theme>) {
  const baseTooltipSx = {
    ...baseTooltipStyles,
    ...lightVariantStyles,
    outline: getLightOutline(),
    ...baseArrowStyles,
  };

  return {
    slotProps: {
      popper: {
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -8],
            },
          },
        ],
      },
      tooltip: {
        sx: mixinSx(baseTooltipSx, additionalSx),
      },
    },
  };
}
